import React, { createContext, useContext, useEffect, useState } from "react";

// Minimal EN/VI dictionary for UI strings. AI handles content translation.
const STRINGS = {
  en: {
    "brand.tagline": "AN AI READING ASSISTANT",
    "nav.signin": "Sign in with Google",
    "nav.dashboard": "Open dashboard",
    "hero.title.1": "Read less.",
    "hero.title.2": "Understand",
    "hero.title.3": "more.",
    "hero.body": "Drop in a link, a PDF, pasted text or a YouTube video. Readlet distils the main idea, key findings and a considered conclusion — in the time it takes to pour a coffee.",
    "hero.cta": "Start reading smarter",
    "hero.note": "Free while in beta · No credit card",
    "features.tag": "Four ways in",
    "features.title": "Everything you read, distilled.",
    "features.body": "Whether it's a research paper you've been avoiding or a keynote you missed — paste, upload, or link. Readlet reads it in seconds.",
    "cta.ready": "Ready when you are.",
    "cta.body": "Sign in with Google and try your first summary in under a minute.",
    "dash.hello": "Hello",
    "dash.title": "What are you reading today?",
    "dash.subtitle": "Pick a source and Readlet will hand back the essence in a minute.",
    "dash.model": "Model",
    "dash.summarise": "Summarise",
    "dash.reading": "Reading…",
    "library.title": "Library",
    "library.empty": "Your summaries will live here.",
    "summary.main": "Main idea",
    "summary.findings": "Key findings",
    "summary.conclusion": "Conclusion",
    "summary.full": "Full summary",
    "summary.translate.to.vi": "Translate to Vietnamese",
    "summary.translate.to.en": "Show original",
    "summary.translating": "Translating…",
    "meeting.record": "Start recording",
    "meeting.stop": "Stop & transcribe",
    "meeting.recording": "Recording…",
    "meeting.processing": "Transcribing with Whisper…",
    "meeting.hint": "Make sure you have permission to record the conversation.",
    "meeting.transcript": "Transcript",
    "bot.title": "Lumi",
    "bot.subtitle": "Your reading buddy",
    "bot.placeholder": "Ask Lumi anything…",
    "bot.send": "Send",
    "bot.empty": "Hi! Ask me anything about what you're reading — or how to use Readlet.",
    "pwa.install": "Install app",
  },
  vi: {
    "brand.tagline": "TRỢ LÝ ĐỌC AI",
    "nav.signin": "Đăng nhập với Google",
    "nav.dashboard": "Mở bảng điều khiển",
    "hero.title.1": "Đọc ít.",
    "hero.title.2": "Hiểu",
    "hero.title.3": "nhiều hơn.",
    "hero.body": "Dán một liên kết, PDF, đoạn văn hoặc video YouTube. Readlet rút ra ý chính, kết quả then chốt và kết luận rõ ràng — chỉ trong vài giây.",
    "hero.cta": "Bắt đầu đọc thông minh",
    "hero.note": "Miễn phí trong bản beta · Không cần thẻ",
    "features.tag": "Bốn cách nhập",
    "features.title": "Mọi thứ bạn đọc, được cô đọng.",
    "features.body": "Dù là bài nghiên cứu bạn chưa đọc hay bài diễn thuyết bạn bỏ lỡ — dán, tải lên hoặc gửi liên kết. Readlet đọc trong vài giây.",
    "cta.ready": "Sẵn sàng khi bạn muốn.",
    "cta.body": "Đăng nhập bằng Google và thử bản tóm tắt đầu tiên trong chưa đầy một phút.",
    "dash.hello": "Xin chào",
    "dash.title": "Hôm nay bạn đang đọc gì?",
    "dash.subtitle": "Chọn một nguồn, Readlet sẽ trả về phần cốt lõi trong một phút.",
    "dash.model": "Mô hình",
    "dash.summarise": "Tóm tắt",
    "dash.reading": "Đang đọc…",
    "library.title": "Thư viện",
    "library.empty": "Các bản tóm tắt sẽ nằm ở đây.",
    "summary.main": "Ý chính",
    "summary.findings": "Điểm chính",
    "summary.conclusion": "Kết luận",
    "summary.full": "Tóm tắt đầy đủ",
    "summary.translate.to.vi": "Dịch sang tiếng Việt",
    "summary.translate.to.en": "Xem bản gốc",
    "summary.translating": "Đang dịch…",
    "meeting.record": "Bắt đầu ghi âm",
    "meeting.stop": "Dừng & phiên âm",
    "meeting.recording": "Đang ghi âm…",
    "meeting.processing": "Đang phiên âm bằng Whisper…",
    "meeting.hint": "Hãy chắc chắn bạn có quyền ghi âm cuộc trò chuyện.",
    "meeting.transcript": "Bản phiên âm",
    "bot.title": "Lumi",
    "bot.subtitle": "Người bạn đọc của bạn",
    "bot.placeholder": "Hỏi Lumi bất cứ điều gì…",
    "bot.send": "Gửi",
    "bot.empty": "Xin chào! Hỏi tôi bất cứ điều gì về thứ bạn đang đọc — hoặc cách dùng Readlet.",
    "pwa.install": "Cài đặt ứng dụng",
  },
};

const LanguageContext = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    return localStorage.getItem("lumina-lang") || "en";
  });
  useEffect(() => { localStorage.setItem("lumina-lang", lang); document.documentElement.lang = lang; }, [lang]);
  const setLang = (l) => setLangState(l === "vi" ? "vi" : "en");
  const t = (k) => (STRINGS[lang] && STRINGS[lang][k]) || STRINGS.en[k] || k;
  return <LanguageContext.Provider value={{ lang, setLang, t }}>{children}</LanguageContext.Provider>;
};

export const useLang = () => useContext(LanguageContext);
